import { useCallback, useEffect, useState } from 'react';
import {
  fetchFollowers,
  fetchFollowing,
  fetchFollowStatuses,
  type FollowListItem,
  type PageMeta,
} from '../socialApi';
import { FollowButton } from './FollowButton';
import { UserAvatar } from './UserAvatar';

type FollowListMode = 'followers' | 'following';

type FollowListScreenProps = {
  userId: string;
  mode: FollowListMode;
  authenticated: boolean;
  currentUserId: string | null;
  onBack: () => void;
  onOpenUser: (userId: string) => void;
  onModeChange: (mode: FollowListMode) => void;
};

export function FollowListScreen({
  userId,
  mode,
  authenticated,
  currentUserId,
  onBack,
  onOpenUser,
  onModeChange,
}: FollowListScreenProps) {
  const [items, setItems] = useState<FollowListItem[]>([]);
  const [meta, setMeta] = useState<PageMeta | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (nextPage: number, append: boolean) => {
    setLoading(true);
    setError(null);
    try {
      const response = mode === 'followers'
        ? await fetchFollowers(userId, nextPage, 20)
        : await fetchFollowing(userId, nextPage, 20);

      let enriched = response.data;
      if (authenticated && response.data.length > 0) {
        const ids = response.data.map((item) => item.id);
        const statuses = await fetchFollowStatuses(ids);
        enriched = response.data.map((item) => ({
          ...item,
          you_follow: statuses.data[item.id]?.you_follow ?? false,
          follows_you: statuses.data[item.id]?.follows_you ?? false,
        }));
      }

      setItems((current) => append ? [...current, ...enriched] : enriched);
      setMeta(response.meta);
      setPage(nextPage);
    } catch {
      setError('Не удалось загрузить список. Попробуйте ещё раз.');
    } finally {
      setLoading(false);
    }
  }, [authenticated, mode, userId]);

  useEffect(() => {
    setItems([]);
    setMeta(null);
    setPage(1);
    void load(1, false);
  }, [load]);

  const empty = mode === 'followers' ? 'Пока нет подписчиков' : 'Пока нет подписок';

  return (
    <main className="social-page follow-list-screen">
      <button className="social-back" type="button" onClick={onBack}>← Назад</button>
      <section className="social-page__header"><h1>Подписки и подписчики</h1></section>

      <div className="social-tabs follow-list-tabs" role="tablist" aria-label="Подписчики и подписки">
        <button type="button" className={mode === 'followers' ? 'is-active' : ''} onClick={() => onModeChange('followers')}>Подписчики</button>
        <button type="button" className={mode === 'following' ? 'is-active' : ''} onClick={() => onModeChange('following')}>Подписки</button>
      </div>

      {error ? (
        <section className="social-empty is-error">
          <strong>{error}</strong>
          <button className="social-primary-button" type="button" onClick={() => void load(1, false)}>Повторить</button>
        </section>
      ) : items.length === 0 && !loading ? (
        <section className="social-empty"><strong>{empty}</strong></section>
      ) : (
        <section className="follow-list">
          {items.map((item) => (
            <article className="follow-row" key={item.id}>
              <UserAvatar displayName={item.display_name} size={48} onClick={() => onOpenUser(item.id)} />
              <button type="button" className="follow-row__identity" onClick={() => onOpenUser(item.id)}>
                <strong>{item.display_name?.trim() || 'Гость Hopper'}</strong>
                {item.follows_you && currentUserId !== item.id && <span>Подписан(а) на вас</span>}
              </button>
              {authenticated && currentUserId !== item.id && (
                <FollowButton
                  key={`${item.id}:${item.you_follow ? '1' : '0'}`}
                  userId={item.id}
                  initialFollowing={item.you_follow ?? false}
                  size="small"
                />
              )}
            </article>
          ))}
        </section>
      )}

      {loading && <div className="social-loading">Загружаем…</div>}
      {meta && page < meta.last_page && !loading && (
        <button className="social-load-more" type="button" onClick={() => void load(page + 1, true)}>Показать ещё</button>
      )}
    </main>
  );
}
